/**
 * Parse session expiry time string (e.g., "2h", "120m", "4000h", "1d") and return expiry timestamp
 * @param sessionExpiryTime - Time string like "2h", "120m", "4000h", "1d", etc.
 * @returns Unix timestamp in milliseconds when session expires
 */
export const parseSessionExpiryTime = (sessionExpiryTime: string): number => {
  const now = Date.now();
  
  if (!sessionExpiryTime || typeof sessionExpiryTime !== 'string') {
    // Default to 2 hours if invalid input
    return now + 2 * 60 * 60 * 1000;
  }
  
  // Trim whitespace and match pattern: number followed by unit (d, h, m, s)
  // Supports formats like "120m", "4000h", "2d", "30s", etc.
  const trimmed = sessionExpiryTime.trim();
  const match = trimmed.match(/^(\d+)([dhms])$/i);
  
  if (!match) {
    console.warn(`Invalid session expiry time format: "${sessionExpiryTime}". Defaulting to 2 hours.`);
    // Default to 2 hours if parsing fails
    return now + 2 * 60 * 60 * 1000;
  }

  const value = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();

  let milliseconds = 0;

  switch (unit) {
    case 'd':
      milliseconds = value * 24 * 60 * 60 * 1000; // days
      break;
    case 'h':
      milliseconds = value * 60 * 60 * 1000; // hours
      break;
    case 'm':
      milliseconds = value * 60 * 1000; // minutes
      break;
    case 's':
      milliseconds = value * 1000; // seconds
      break;
    default:
      console.warn(`Unknown session expiry unit: "${unit}". Defaulting to 2 hours.`);
      milliseconds = 2 * 60 * 60 * 1000; // default 2 hours
  }

  return now + milliseconds;
};

/**
 * Get country flag emoji from country code
 * @param countryCode - ISO country code (e.g., "NG", "US")
 * @returns Flag emoji or country code if not found
 */
export const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) {
    return countryCode || '';
  }

  // Convert country code to flag emoji
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  
  return String.fromCodePoint(...codePoints);
};

